/**
 * Universal Collection Grid Slider - Advanced
 * Theme-independent slider with desktop/mobile controls
 */

class UniversalCollectionSliderAdvanced {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('.ucg-slider-track');
    this.items = container.querySelectorAll('.ucg-slider-item');
    this.prevButton = container.querySelector('.ucg-nav-prev');
    this.nextButton = container.querySelector('.ucg-nav-next');
    
    // Get configuration from data attributes
    this.enableDesktop = container.dataset.enableDesktop === 'true';
    this.enableMobile = container.dataset.enableMobile === 'true';
    this.itemsDesktop = parseFloat(container.dataset.itemsDesktop) || 4;
    this.itemsMobile = parseFloat(container.dataset.itemsMobile) || 1.5;
    this.itemsGap = parseInt(container.dataset.gap) || 15;
    
    // State
    this.currentIndex = 0;
    this.isDragging = false;
    this.hasMovedPastThreshold = false;
    this.startX = 0;
    this.currentX = 0;
    this.translateX = 0;
    this.draggedDistance = 0;
    this.wasDragging = false;
    
    // Debounce timer
    this.resizeTimer = null;
    
    if (this.items.length > 0) {
      this.init();
    }
  }

  init() {
    // Check if slider should be active on current viewport
    if (!this.isSliderEnabled()) {
      // Grid mode - no slider functionality needed
      return;
    }
    
    // Set initial state
    this.updateItemWidths();
    this.updateButtonVisibility();
    this.updatePosition(false);
    
    // Setup event listeners
    this.setupEventListeners();
    this.setupTouchEvents();
    this.setupResizeHandler();
    
    // Add loaded class
    this.container.classList.add('ucg-loaded');
  }

  /**
   * Check if slider is enabled on current viewport
   */
  isSliderEnabled() {
    const isMobile = window.innerWidth < 750;
    return isMobile ? this.enableMobile : this.enableDesktop;
  }

  /**
   * Get number of items per view based on viewport
   */
  getItemsPerView() {
    const isMobile = window.innerWidth < 750;
    return isMobile ? this.itemsMobile : this.itemsDesktop;
  }

  /**
   * Update item widths based on items per view
   */
  updateItemWidths() {
    const itemsPerView = this.getItemsPerView();
    const containerWidth = this.getContainerWidth();
    
    // Calculate item width: (container width - gaps) / items per view
    const totalGaps = this.itemsGap * (itemsPerView - 1);
    const itemWidth = (containerWidth - totalGaps) / itemsPerView;
    
    this.items.forEach(item => {
      item.style.flex = `0 0 ${itemWidth}px`;
      item.style.marginRight = `${this.itemsGap}px`;
    });
    
    // Remove margin from last item
    if (this.items.length > 0) {
      this.items[this.items.length - 1].style.marginRight = '0';
    }
  }

  /**
   * Get container width
   */
  getContainerWidth() {
    return this.container.offsetWidth;
  }
  
  /**
   * Calculate total content width including gaps
   */
  getTotalContentWidth() {
    if (this.items.length === 0) return 0;
    
    const totalWidth = Array.from(this.items).reduce((total, item) => {
      const marginRight = parseInt(getComputedStyle(item).marginRight) || 0;
      return total + item.offsetWidth + marginRight;
    }, 0);
    
    return totalWidth;
  }
  
  /**
   * Calculate maximum scroll position
   */
  getMaxScrollPosition() {
    const containerWidth = this.getContainerWidth();
    const contentWidth = this.getTotalContentWidth();
    return Math.max(0, contentWidth - containerWidth);
  }

  /**
   * Update slider position
   */
  updatePosition(animate = true) {
    if (this.items.length === 0 || !this.isSliderEnabled()) return;
    
    const itemWidth = this.items[0].offsetWidth + this.itemsGap;
    const maxScrollPosition = this.getMaxScrollPosition();
    
    // Calculate position based on current index
    let position = this.currentIndex * itemWidth;
    
    // Ensure we don't exceed maximum scroll
    position = Math.min(position, maxScrollPosition);
    
    // Apply transform
    if (!animate) {
      this.track.style.transition = 'none';
      requestAnimationFrame(() => {
        this.track.style.transform = `translateX(${-position}px)`;
        requestAnimationFrame(() => {
          this.track.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1)';
        });
      });
    } else {
      this.track.style.transform = `translateX(${-position}px)`;
    }
    
    this.updateButtonVisibility();
  }

  /**
   * Update navigation button visibility
   */
  updateButtonVisibility() {
    if (!this.prevButton || !this.nextButton || !this.isSliderEnabled()) return;
    
    const maxScrollPosition = this.getMaxScrollPosition();
    
    // Check if content is scrollable
    if (maxScrollPosition <= 0) {
      this.prevButton.classList.remove('ucg-visible');
      this.nextButton.classList.remove('ucg-visible');
      return;
    }
    
    // Previous button
    if (this.currentIndex === 0) {
      this.prevButton.classList.remove('ucg-visible');
    } else {
      this.prevButton.classList.add('ucg-visible');
    }

    // Next button
    const itemWidth = this.items[0]?.offsetWidth + this.itemsGap || 0;
    const currentPosition = this.currentIndex * itemWidth;
    
    if (currentPosition >= maxScrollPosition - 10) {
      this.nextButton.classList.remove('ucg-visible');
    } else {
      this.nextButton.classList.add('ucg-visible');
    }
  }

  /**
   * Setup click event listeners
   */
  setupEventListeners() {
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.goToNext());
    }

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.goToPrev());
    }
  }
  
  /**
   * Navigate to next slide
   */
  goToNext() {
    if (this.items.length === 0 || !this.isSliderEnabled()) return;
    
    const itemWidth = this.items[0].offsetWidth + this.itemsGap;
    const maxScrollPosition = this.getMaxScrollPosition();
    const currentPosition = this.currentIndex * itemWidth;
    
    if (currentPosition < maxScrollPosition) {
      this.currentIndex++;
      this.updatePosition();
    }
  }
  
  /**
   * Navigate to previous slide
   */
  goToPrev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updatePosition();
    }
  }

  /**
   * Setup touch and mouse drag events
   */
  setupTouchEvents() {
    if (!this.isSliderEnabled()) return;
    
    // Single capture-phase click guard on the track
    // This replaces per-drag listener stacking with a simple flag check
    this.track.addEventListener('click', (e) => {
      if (this.wasDragging) {
        e.preventDefault();
        e.stopPropagation();
        this.wasDragging = false;
      }
    }, true);
    
    // Touch events for mobile
    this.track.addEventListener('touchstart', (e) => {
      this.handleDragStart(e.touches[0].clientX);
    }, { passive: true });
    
    this.track.addEventListener('touchmove', (e) => {
      if (this.isDragging) {
        e.preventDefault();
      }
      this.handleDragMove(e.touches[0].clientX);
    }, { passive: false });
    
    this.track.addEventListener('touchend', () => {
      this.handleDragEnd();
    });
    
    this.track.addEventListener('touchcancel', () => {
      this.handleDragEnd();
    });
    
    // Mouse events for desktop
    this.track.addEventListener('mousedown', (e) => {
      // Only respond to left mouse button
      if (e.button !== 0) return;
      
      e.preventDefault();
      this.handleDragStart(e.clientX);
    });
    
    // Mouse move - only attached to document to track dragging outside slider
    this.handleMouseMove = (e) => {
      this.handleDragMove(e.clientX);
    };
    
    this.handleMouseUp = () => {
      this.handleDragEnd();
    };
    
    // Prevent context menu during drag
    this.track.addEventListener('contextmenu', (e) => {
      if (this.isDragging) {
        e.preventDefault();
      }
    });
    
    // Prevent image dragging
    this.track.addEventListener('dragstart', (e) => {
      e.preventDefault();
    });
  }

  /**
   * Handle drag start
   */
  handleDragStart(clientX) {
    if (!this.isSliderEnabled()) return;
    
    this.startX = clientX;
    this.currentX = clientX;
    this.draggedDistance = 0;
    this.hasMovedPastThreshold = false;
    
    // Get current transform value
    const style = window.getComputedStyle(this.track);
    const transform = style.getPropertyValue('transform');
    
    if (transform && transform !== 'none') {
      const matrix = new DOMMatrix(transform);
      this.translateX = matrix.m41;
    } else {
      this.translateX = 0;
    }
    
    // Attach document-level listeners for mouse drag
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  }
  
  /**
   * Handle drag move
   */
  handleDragMove(clientX) {
    if (!this.isSliderEnabled()) return;
    
    // Calculate total distance from start
    const totalDelta = clientX - this.startX;
    
    // Only activate dragging mode after 5px movement threshold
    if (!this.hasMovedPastThreshold && Math.abs(totalDelta) > 5) {
      this.hasMovedPastThreshold = true;
      this.isDragging = true;
      
      // Disable transition for smooth dragging
      this.track.style.transition = 'none';
      this.track.classList.add('ucg-dragging');
      
      // wasDragging flag will be set in handleDragEnd
    }
    
    // Only process drag movement if past threshold
    if (!this.hasMovedPastThreshold) return;
    
    // Calculate incremental movement
    const deltaX = clientX - this.currentX;
    this.currentX = clientX;
    this.draggedDistance = totalDelta;
    
    // Update position
    this.translateX += deltaX;
    
    // Apply boundaries with resistance effect
    const maxTranslate = 0;
    const minTranslate = -this.getMaxScrollPosition();
    
    if (this.translateX > maxTranslate) {
      // Rubber band effect at the start
      this.translateX = maxTranslate + (this.translateX - maxTranslate) * 0.2;
    } else if (this.translateX < minTranslate) {
      // Rubber band effect at the end
      this.translateX = minTranslate + (this.translateX - minTranslate) * 0.2;
    }
    
    // Apply transform
    this.track.style.transform = `translateX(${this.translateX}px)`;
  }
  
  /**
   * Prevent click on links after drag
   */
  preventClick(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    return false;
  }
  
  /**
   * Handle drag end
   */
  handleDragEnd() {
    if (!this.isSliderEnabled()) return;
    
    // Remove document-level mouse listeners
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
    
    // If user didn't drag past threshold, treat as a click
    if (!this.hasMovedPastThreshold) {
      this.isDragging = false;
      return;
    }
    
    // User did drag, so finish the drag interaction
    this.isDragging = false;
    this.wasDragging = true;
    this.track.classList.remove('ucg-dragging');
    
    // Fallback: reset wasDragging after a short delay in case
    // no click event fires (e.g. touchend without a synthetic click)
    setTimeout(() => {
      this.wasDragging = false;
    }, 300);
    
    // Restore transition
    this.track.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1)';
    
    // Snap to nearest slide based on drag distance
    if (this.items.length > 0) {
      const itemWidth = this.items[0].offsetWidth + this.itemsGap;
      const threshold = itemWidth * 0.2;
      
      if (Math.abs(this.draggedDistance) > threshold) {
        if (this.draggedDistance > 0) {
          // Dragged right - go to previous
          this.currentIndex = Math.max(0, this.currentIndex - 1);
        } else {
          // Dragged left - go to next
          const maxIndex = Math.ceil(this.getMaxScrollPosition() / itemWidth);
          this.currentIndex = Math.min(maxIndex, this.currentIndex + 1);
        }
      }
    }
    
    // Reset state
    this.draggedDistance = 0;
    this.hasMovedPastThreshold = false;
    
    // Update position with animation
    this.updatePosition(true);
  }

  /**
   * Setup resize handler with debouncing
   */
  setupResizeHandler() {
    window.addEventListener('resize', () => {
      // Clear existing timer
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      
      // Set new timer
      this.resizeTimer = setTimeout(() => {
        this.handleResize();
      }, 150);
    });
  }

  /**
   * Handle window resize
   */
  handleResize() {
    // Check if slider should be enabled after resize
    if (!this.isSliderEnabled()) {
      // Reset transform if switching to grid mode
      this.track.style.transform = '';
      this.track.style.transition = '';
      
      // Hide buttons
      if (this.prevButton) this.prevButton.classList.remove('ucg-visible');
      if (this.nextButton) this.nextButton.classList.remove('ucg-visible');
      return;
    }
    
    // Update item widths for new viewport
    this.updateItemWidths();
    this.updateButtonVisibility();
    
    // Recalculate position to prevent blank spaces
    if (this.items.length > 0) {
      const itemWidth = this.items[0].offsetWidth + this.itemsGap;
      const maxScrollPosition = this.getMaxScrollPosition();
      const currentPosition = this.currentIndex * itemWidth;
      
      // If current position exceeds max, adjust index
      if (currentPosition > maxScrollPosition) {
        this.currentIndex = Math.floor(maxScrollPosition / itemWidth);
      }
      
      this.updatePosition(false);
    }
  }

  /**
   * Destroy slider and remove event listeners
   */
  destroy() {
    // Remove event listeners
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.goToNext);
    }
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.goToPrev);
    }
    
    // Remove document-level mouse event listeners
    if (this.handleMouseMove) {
      document.removeEventListener('mousemove', this.handleMouseMove);
    }
    if (this.handleMouseUp) {
      document.removeEventListener('mouseup', this.handleMouseUp);
    }
    
    // Clear timers
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    
    // Reset styles
    this.track.style.transform = '';
    this.track.style.transition = '';
    this.track.classList.remove('ucg-dragging');
    
    // Reset item styles
    this.items.forEach(item => {
      item.style.flex = '';
      item.style.marginRight = '';
    });
  }
}

/**
 * Initialize all sliders on the page
 */
function initUniversalCollectionSlidersAdvanced() {
  const sliderContainers = document.querySelectorAll('.ucg-slider-container');
  
  sliderContainers.forEach(container => {
    // Check if already initialized
    if (!container.ucgSlider) {
      container.ucgSlider = new UniversalCollectionSliderAdvanced(container);
    }
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initUniversalCollectionSlidersAdvanced);
} else {
  initUniversalCollectionSlidersAdvanced();
}

// Re-initialize on Shopify section load (theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    const container = event.target.querySelector('.ucg-slider-container');
    if (container) {
      // Destroy existing instance if any
      if (container.ucgSlider) {
        container.ucgSlider.destroy();
      }
      // Create new instance
      container.ucgSlider = new UniversalCollectionSliderAdvanced(container);
    }
  });
  
  document.addEventListener('shopify:section:unload', function(event) {
    const container = event.target.querySelector('.ucg-slider-container');
    if (container && container.ucgSlider) {
      container.ucgSlider.destroy();
      delete container.ucgSlider;
    }
  });
}
